import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { LayoutTemplate, X } from 'lucide-react';
import { boardPath, type BoardListState } from '@molt/shared';
import { Dialog } from '@/components/Dialog';
import { IconButton } from '@/components/IconButton';
import { BoardCreateForm } from '@/components/boards/BoardCreateForm';
import { BoardStateTabs } from '@/components/boards/BoardStateTabs';
import {
  BoardsLanding,
  type BoardsLandingProps,
} from '@/components/boards/BoardsLanding';
import { HomePage } from '@/components/personal/HomePage';
import { announce } from '@/lib/announce';
import { useReorderBoardStar, useToggleBoardStar } from '@/lib/board-stars';
import { useCreateBoard } from '@/lib/boards';
import {
  boardListStateFrom,
  joinWorkspaceGroups,
  useBoardsPage,
  workspaceScopeFrom,
  type LandingGroup,
} from '@/lib/boards-page';
import { useSetChecklistItemChecked } from '@/lib/checklists';
import { useOnboardingBoardPath, useProvisionWelcomeBoard } from '@/lib/onboarding';
import { useCreateComment } from '@/lib/comments';
import { useHome } from '@/lib/home';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { queryKeys } from '@/lib/query-keys';
import { useCreateWorkspace, useWorkspaces } from '@/lib/workspaces';
import { cn } from '@/lib/cn';

/**
 * `/` — the boards landing page. `FE-23.1`, `FR-NOTIF-029`, chunk `D2`,
 * agent G.
 *
 * **This file replaces B05's `StatusPage` outright.** That page's docstring
 * said *"Delete this page when there is a real one; keep the seam"* — the seam
 * it meant is a client fetch through TanStack Query parsed with a
 * `@molt/shared` schema, and this page does exactly that, twice. A health
 * panel on the product's home page is not a seam; it is a status page nobody
 * asked for.
 *
 * The composition site for `BoardsLanding`: the component is presentational
 * and owns one piece of state (the name filter), and this file owns the two
 * queries, the create mutation, the search params and the create
 * announcement. `routes/profile.tsx` is the shipped precedent for the split.
 *
 * ---------------------------------------------------------------------------
 * ⚠ TWO ROUTES, LEFT-JOINED, AND THE JOIN IS THE POINT
 * ---------------------------------------------------------------------------
 * `GET /boards?state=open` is an **INNER JOIN** boards→workspaces, so **a
 * workspace with no boards produces no group at all** — and that is exactly
 * the state `FR-PERM-004` AC2 asserts and `FR-EXP-034` names. `GET
 * /workspaces` is the only route that knows about it. So this page calls
 * both and hands `joinWorkspaceGroups` the pair.
 *
 * ⚠ **Nothing here sorts.** Both routes order by `workspaces.created_at,
 * workspaces.id`, so appending the workspaces that produced no group in their
 * own order keeps one consistent sequence. A client-side alphabetical sort
 * breaks it silently and only once a member has three workspaces.
 * `boards-page.test.ts` holds an assertion that fails if anyone adds one.
 *
 * ---------------------------------------------------------------------------
 * ⚠ `validateSearch` CANNOT STRIP A KEY IT REJECTS
 * ---------------------------------------------------------------------------
 * TanStack Router builds a match's search as `{ ...parentSearch,
 * ...strictSearch }`, so `?state=17` reaches the validator, is rejected, and
 * **still arrives at `useSearch()` as the number `17`**. D1 found this on
 * `/sign-in` and recorded that every route taking search params afterwards
 * meets it; this is that route, and it takes two keys.
 *
 * So the declared type is a convenience, not a runtime guarantee: both values
 * are narrowed **again at the point of use**, through `boardListStateFrom`
 * and `workspaceScopeFrom`. The narrowers are total functions over `unknown`
 * and are unit-tested against a number, an array, `null`, `undefined`, `''`
 * and a bogus string.
 *
 * ---------------------------------------------------------------------------
 * THE TWO SEAMS `D2` LEFT OPEN HERE, BOTH CLOSED BY `D3` (`FE-08.1`/`FE-08.3`)
 * ---------------------------------------------------------------------------
 *  - **The closed-boards surface.** `D2` already read `?state=open|closed` and
 *    passed it to the query while rendering no toggle at all. `D3` mounts
 *    `BoardStateTabs` — **here**, not inside `BoardsLanding`, because the tabs
 *    are TanStack `Link`s and need a router that the presentational component's
 *    tests do not mount; it takes them as a required `tabs` node and renders
 *    them under its own `<h1>`, in every branch.
 *  - **"Create a board".** The button lives in each eligible group's header
 *    (`BoardGroup`), this route hosts the form in a `Dialog`, owns
 *    `useCreateBoard`, announces, and navigates to the new board —
 *    `FR-BOARD-001` AC2, *"opened directly on the board face"*. The
 *    post-creation focus target after a **workspace** is created is now that
 *    button rather than the group's `<h2>`, which is what `entry-surfaces.md`
 *    **D4** asked for in the first place.
 *
 * ---------------------------------------------------------------------------
 * ⚠ `creatableWorkspaceIds` COMES FROM `useWorkspaces()`, NEVER FROM `groups`
 * ---------------------------------------------------------------------------
 * `D3-BRIEF.md` §5.2, and it is the one thing on this page that would be
 * wrong with nothing red. `boardsPageSchema.groups` is built from boards the
 * caller can **see**: a guest holding a single `board_members` row produces a
 * group for a workspace they are **not** a member of, and `POST /boards` calls
 * `requireWorkspaceMember` and answers **404**. `FR-EXP-034` forbids an action
 * button the member cannot use, so the membership-scoped query is the only
 * honest source — and while it is still loading it supplies no ids at all, so
 * no button is drawn.
 *
 * `boardGroupSchema` still has no `canCreateBoard` field. That is the third
 * report of the same missing caller-capability gap (§3.3), not a thing to
 * patch in `packages/shared`.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE 401 REDIRECT'S FIRST LIVE CALLER — AND IT IS NOT HANDLED HERE
 * ---------------------------------------------------------------------------
 * `GET /boards` and `GET /workspaces` both call `requireUser` unconditionally,
 * so this is the first page in `apps/web` whose queries can 401. That is
 * already bound, once, in `main.tsx`'s `QueryCache.onError`, which navigates
 * to `signInHref(window.location)` with `replace: true`. **There is
 * deliberately no second handler here and no `throwOnError`** — D1 measured
 * that `throwOnError: false` does not suppress the cache callback
 * (`query-core@5.101.4`, `query.js:323`); the exclusion is by query key and
 * only `['me']` is excluded.
 *
 * Two queries 401 together for a signed-out visitor, so the callback fires
 * **twice**, and this is the first place in the product where that happens.
 *
 * ⚠ **This paragraph used to claim both firings produce the identical
 * `/sign-in?next=%2F` and that the second is idempotent. Both halves were
 * false**, and the browser pass measured it: `signInHref` *omits* `next`
 * entirely when the path is `/`, so there was never a `next=%2F` to produce;
 * and the second callback read a URL the first had already rewritten, yielding
 * a `next` that pointed back at the sign-in page. See the `validateSearch`
 * note below for the cause and the fix.
 *
 * It is true **now**, once each search key is omitted at its default: `/`
 * stays `/`, so both firings navigate to the same bare `/sign-in` and the
 * second is genuinely idempotent. Recorded at length because a wrong claim in
 * the file that owns the behaviour is worse than no claim — a later reader
 * trusts it.
 */

/**
 * ⚠ **Both keys are declared OPTIONAL, and that is a correction to
 * `D2-BRIEF.md` §6.7 rather than a preference.**
 *
 * §6.7's fixed signature types this route's search as
 * `{ state: BoardListState; workspace: string | null }` — both required. A
 * route whose search type has a **required** key makes `search` a required
 * prop on every `<Link to="/">` in the product (`MakeRequiredSearchParams`,
 * `@tanstack/router-core` `link.d.ts:152`), and two shipped files link to `/`
 * with no search at all: `components/shell/Header.tsx`'s brand link and
 * `components/shell/RouteNotFound.tsx`'s *"Back to your boards"*. Written the
 * brief's way, `pnpm typecheck` fails in two files this chunk does not own,
 * and the only alternative fixes are to edit both of them and to force every
 * future link to the home page to carry a search object.
 *
 * **Nothing about the runtime changes**: the validator still returns both
 * keys, always, with concrete values. Only the declared type is relaxed, and
 * every read narrows again anyway (§5.4) — so the optionality costs a caller
 * nothing it was not already obliged to do.
 */
interface BoardsSearch {
  /** `?state=open|closed`. `FE-08.3` (D3) adds the control; this route only reads it. */
  state?: BoardListState;
  /** `?workspace=<ULID>` — §5.8. `null` is *All workspaces*, the default. */
  workspace?: string | null;
}

export const Route = createFileRoute('/')({
  /* Two columns — the boards and Home beside them — so the header's width
     rather than the reading column's; see `ShellLayout`'s `'wide'`. */
  staticData: { layout: 'wide' },
  /*
   * A total function, never a bare schema and never a throw: `validateSearch`
   * runs during *matching*, so a throw is the whole route failing to match and
   * the member landing in `RouteError` because somebody sent them
   * `/?state[]=x`. Both narrowers degrade a malformed value to its default.
   * `routes/sign-in.tsx` is the shipped precedent.
   */
  /*
   * ⚠ **Each key is OMITTED at its default rather than written back**, and
   * that is a fix for a defect the browser pass found, not a stylistic choice.
   *
   * `validateSearch`'s return value is what TanStack Router writes into the
   * address bar. Returning both keys unconditionally meant **`/` normalised
   * itself to `/?state=open&workspace=null` on arrival** — the product's home
   * page, for every visitor, carrying the string `null`.
   *
   * That was not merely untidy. `signInHref` omits `next` when the path is
   * exactly `/`, so the rewritten URL made that branch **dead on this route**;
   * and because `GET /boards` and `GET /workspaces` 401 together, the second
   * `QueryCache.onError` built its `next` from the URL the first had already
   * written. A signed-out visitor landed on
   * `/sign-in?next=%2Fsign-in%3Fnext%3D…` — a `next` pointing at the sign-in
   * page. `safeNextPath` maps that back to `/` so the destination was right,
   * which is exactly why nothing was red: the defect was visible only in the
   * address bar.
   *
   * Omitting a key at its default leaves `/` as `/`, restores the omit-`next`
   * branch, and makes the two 401s produce the identical bare `/sign-in`.
   */
  validateSearch: (search: Record<string, unknown>): BoardsSearch => {
    const state = boardListStateFrom(search.state);
    const workspace = workspaceScopeFrom(search.workspace);
    return {
      ...(state === 'open' ? {} : { state }),
      ...(workspace === null ? {} : { workspace }),
    };
  },
  component: BoardsRoute,
});

function BoardsRoute() {
  const { t } = useInterfaceLanguage();
  const search = Route.useSearch();
  // Narrowed AGAIN, at the point of use — see the docstring. The declared
  // type of `search` is not a runtime guarantee.
  const state = boardListStateFrom(search.state);
  const workspaceScope = workspaceScopeFrom(search.workspace);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const boards = useBoardsPage(state);
  const workspaces = useWorkspaces();
  /*
   * ⚠ ONE WORKSPACE SCOPE, NOT TWO. Highlights used to carry their own
   * component-state filter (B76 brief :63-64), so the shell's workspace
   * switcher scoped the boards to one workspace while the Highlights beside
   * them still read "All workspaces". `?workspace=` now drives both, and the
   * Highlights `<select>` navigates rather than setting local state —
   * `FR-NOTIF-028` AC :393 asks only that Highlights *can* be filtered.
   *
   * Guarded against the workspace list for the same reason `scopeToWorkspace`
   * falls back: a stale or hand-typed id must read as "All", not as an empty
   * feed. And held until that list resolves whenever a scope is present —
   * otherwise `/me/home` fetches unscoped, then again scoped, and the feed
   * visibly swaps under the reader.
   */
  const highlightsScope =
    workspaceScope !== null &&
    (workspaces.data ?? []).some((workspace) => workspace.id === workspaceScope)
      ? workspaceScope
      : null;
  const home = useHome(
    highlightsScope === null ? {} : { highlightsWorkspaceId: highlightsScope },
    { enabled: workspaceScope === null || !workspaces.isPending },
  );
  const createWorkspace = useCreateWorkspace();
  const createBoard = useCreateBoard();
  const toggleBoardStar = useToggleBoardStar();
  const reorderBoardStar = useReorderBoardStar();
  const provisionWelcomeBoard = useProvisionWelcomeBoard();
  const onboardingPath = useOnboardingBoardPath();
  const createComment = useCreateComment();
  const checkChecklistItem = useSetChecklistItemChecked(t);

  const [creating, setCreating] = useState(false);
  const [focusGroupId, setFocusGroupId] = useState<string | null>(null);
  const [replyingHighlightId, setReplyingHighlightId] = useState<string | null>(null);
  /**
   * The workspace whose *Create a board* button opened the dialog, or `null`
   * when it is closed. One state variable, not two: an `open` boolean beside a
   * `workspaceId` can disagree, and the disagreement renders a dialog with no
   * destination.
   */
  const [createBoardIn, setCreateBoardIn] = useState<string | null>(null);
  /** ⚠ `Dialog.initialFocus` aims at `BoardCreateForm`'s real title input. */
  const boardTitleFieldRef = useRef<HTMLInputElement | null>(null);

  const joined = joinWorkspaceGroups(boards.data?.groups ?? [], workspaces.data);
  const groups = scopeToWorkspace(joined, workspaceScope);
  const starredBoards = boards.data?.starred ?? [];
  const recentBoards = boards.data?.recent ?? [];

  /*
   * `isPending` on BOTH queries, and the pair is load-bearing in two places.
   *
   *  1. A member with workspaces but no boards would otherwise see the
   *     first-run "create your first workspace" copy for a frame, because
   *     `GET /boards` can resolve to zero groups before `GET /workspaces`
   *     has said the workspaces exist.
   *  2. `scopeToWorkspace`'s fallback (below) is only flicker-free because of
   *     this. Narrow `loading` to the boards query alone and a valid
   *     `?workspace=` naming a boards-less workspace renders every group for
   *     a frame before snapping to one.
   *
   * `isPending` rather than `isFetching`: it is true only for the first load,
   * so a background refetch does not blank a page that already has content.
   */
  const loading = boards.isPending || workspaces.isPending;

  /*
   * P2-33 / FE-46.1: B46's welcome board is provisioned only from the exact
   * first-run landing state. The old "Create a workspace" empty state remains
   * the fallback after suppression, not-needed, or failure; it must not flash
   * while the onboarding POST has not had its say.
   */
  const firstRunOnboardingCandidate =
    state === 'open' &&
    workspaceScope === null &&
    !loading &&
    boards.error === null &&
    workspaces.error === null &&
    (boards.data?.groups.length ?? 0) === 0 &&
    (workspaces.data?.length ?? 0) === 0;
  const holdForOnboarding =
    firstRunOnboardingCandidate &&
    (provisionWelcomeBoard.isIdle ||
      provisionWelcomeBoard.isPending ||
      onboardingPath.isPending);

  useEffect(() => {
    if (!firstRunOnboardingCandidate) return;
    if (!provisionWelcomeBoard.isIdle) return;

    provisionWelcomeBoard.mutate(undefined, {
      onSuccess: (result) => {
        if (result.status === 'created' || result.status === 'existing') {
          announce(t('home.announce.welcomeBoardReady'));
          onboardingPath.mutate(result.boardId, {
            onSuccess: (href) => {
              void navigate({ href, replace: true });
            },
          });
          return;
        }

        void boards.refetch();
        void workspaces.refetch();
      },
    });
  }, [
    boards,
    firstRunOnboardingCandidate,
    navigate,
    onboardingPath,
    provisionWelcomeBoard,
    t,
    workspaces,
  ]);

  /*
   * `exactOptionalPropertyTypes`: both props are declared `foo?: string`, so
   * an explicit `undefined` does not satisfy them — only an absent key does.
   * Typed against `BoardsLandingProps` rather than left as a bare object
   * literal, because a JSX spread bypasses excess-property checking and a
   * typo'd key would then compile to nothing at all.
   *
   * `error` takes the boards failure first: it is the query that carries the
   * page's content, and `onRetry` refetches both regardless. A workspaces-only
   * failure does put the page into the failed render while the boards it
   * fetched are technically in hand — accepted, because §6.7 fixes five
   * mutually exclusive renders and a half-page with a hidden failure is the
   * state `FR-EXP-034` exists to forbid.
   */
  const optionalProps: Pick<BoardsLandingProps, 'error' | 'createError'> = {
    ...(boards.error === null && workspaces.error === null
      ? {}
      : {
          error:
            (boards.error ?? workspaces.error)?.message ?? t('home.error.requestFailed'),
        }),
    ...(createWorkspace.error === null
      ? {}
      : { createError: createWorkspace.error.message }),
  };

  function retryLanding(): void {
    void boards.refetch();
    void workspaces.refetch();
  }

  function retryHome(): void {
    void home.refetch();
  }

  /*
   * `FE-08.1` — see the docstring's own section. `?? []` is what satisfies
   * "draw no button while `GET /workspaces` is still in flight": a pending
   * query has no `data`, so the set is empty and every group's
   * `canCreateBoard` is false. No second `isPending` guard — and in this
   * composed route that frame is not even reachable, because `loading` covers
   * both queries and renders the skeleton instead.
   */
  const creatableWorkspaces = workspaces.data ?? [];
  const creatableWorkspaceIds: ReadonlySet<string> = new Set(
    creatableWorkspaces.map((workspace) => workspace.id),
  );

  function closeCreateBoard() {
    setCreateBoardIn(null);
    createBoard.reset();
  }

  return (
    <>
      {/*
        Boards first, Home beside them. The boards are this page's job
        (`FR-NOTIF-029` is Phase 1; Home is Parity-Later) and the product
        tie-breaker says a board must stay readable at a glance — so when Home
        sat above the boards, a busy Highlights feed pushed every board below
        the fold. The order is the DOM's, not CSS's, so reading order and focus
        order match the screen; on narrow viewports Home stacks underneath.
      */}
      <div className="grid w-full gap-8 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
        <BoardsLanding
          className="mx-0 max-w-none py-0"
          groups={groups}
          starredBoards={starredBoards}
          recentBoards={recentBoards}
          state={state}
          tabs={<BoardStateTabs state={state} />}
          templateGalleryLink={
            <Link
              to="/templates"
              className={cn(
                'inline-flex h-9 items-center justify-center gap-2 rounded-card border border-border-strong bg-surface-raised px-3 text-card font-medium text-ink transition-[filter,background-color,color]',
                'hover:brightness-95 dark:hover:brightness-110',
              )}
            >
              <LayoutTemplate aria-hidden="true" className="size-4" />
              {t('boards.templateGallery')}
            </Link>
          }
          loading={loading || holdForOnboarding}
          creatableWorkspaceIds={creatableWorkspaceIds}
          onCreateBoard={(workspaceId) => {
            createBoard.reset();
            setCreateBoardIn(workspaceId);
          }}
          onToggleBoardStar={(board, starred) => {
            toggleBoardStar.mutate({ board, starred });
          }}
          onReorderStarredBoard={(boardId, neighbours) => {
            reorderBoardStar.mutate({
              boardId,
              previousBoardId: neighbours.previousBoardId,
              nextBoardId: neighbours.nextBoardId,
            });
          }}
          onRetry={retryLanding}
          creating={creating}
          onStartCreating={() => {
            setCreating(true);
          }}
          onCancelCreating={() => {
            setCreating(false);
          }}
          onCreateWorkspace={(name) => {
            /*
             * The announcement is passed per call, not added to the hook:
             * `useCreateWorkspace` already declares an `onSuccess` that APPENDS
             * the new workspace to `['workspaces']`, and both callbacks fire.
             * Declaring a second one on the hook would replace the cache write —
             * and that write is the whole reason the new group appears here.
             *
             * ⚠ **No refetch, and none should be added.** The group arrives
             * because the cache was written and `joinWorkspaceGroups` left-joins
             * it in; a fresh `GET /boards` would return the same INNER JOIN with
             * the same missing group.
             */
            createWorkspace.mutate(name, {
              onSuccess: (created) => {
                const workspaceName =
                  created.name.trim() === ''
                    ? t('boards.untitledWorkspace')
                    : created.name;
                announce(
                  t('home.announce.workspaceCreated', { workspace: workspaceName }),
                );
                setCreating(false);
                setFocusGroupId(created.id);
              },
            });
          }}
          createBusy={createWorkspace.isPending}
          focusGroupId={focusGroupId}
          {...optionalProps}
        />
        <HomePage
          home={home.data}
          loading={home.isPending}
          error={home.error?.message ?? null}
          workspaceFilterId={highlightsScope}
          highlightWorkspaceOptions={(workspaces.data ?? []).map((workspace) => ({
            id: workspace.id,
            name: workspace.name,
          }))}
          replyingHighlightId={replyingHighlightId}
          checkingItemId={checkChecklistItem.variables?.itemId ?? null}
          mutationError={
            createComment.error?.message ??
            (checkChecklistItem.error instanceof Error
              ? checkChecklistItem.error.message
              : null)
          }
          onRetry={retryHome}
          onWorkspaceFilterChange={(workspaceId) => {
            void navigate({
              to: '/',
              search: (prev) => ({ ...prev, workspace: workspaceId }),
            });
          }}
          onReplyToHighlight={(highlightId, cardId, boardId, body) => {
            setReplyingHighlightId(highlightId);
            createComment.mutate(
              { boardId, cardId, body },
              {
                onSuccess: () => {
                  announce(t('home.announce.replyPosted'));
                  void queryClient.invalidateQueries({ queryKey: queryKeys.meHome() });
                },
                onSettled: () => {
                  setReplyingHighlightId(null);
                },
              },
            );
          }}
          onCheckAssignedItem={(itemId, cardId, boardId) => {
            checkChecklistItem.mutate(
              { itemId, cardId, boardId, checked: true },
              {
                onSuccess: () => {
                  announce(t('home.announce.checklistItemChecked'));
                  void queryClient.invalidateQueries({ queryKey: queryKeys.meHome() });
                },
              },
            );
          }}
        />
      </div>

      {/*
        `FE-08.1` — the create-board dialog. It lives here rather than in
        `BoardsLanding` for the same reason the tabs do: this route owns the
        mutation, the announcement and the navigation, and the component owns
        the page. `Dialog` is the right primitive here (unlike for the board
        *menu*, `D3-BRIEF.md` §2.1) — creation is a focused, modal act with a
        confirm, and the board list behind it is not something the member acts
        on meanwhile.
      */}
      <Dialog
        open={createBoardIn !== null}
        onOpenChange={(open) => {
          if (!open) closeCreateBoard();
        }}
        title={t('boards.createBoard')}
        initialFocus={boardTitleFieldRef}
        closeControl={
          <IconButton label={t('boards.createDialog.close')}>
            <X aria-hidden="true" className="size-4" />
          </IconButton>
        }
      >
        <BoardCreateForm
          ref={boardTitleFieldRef}
          workspaces={creatableWorkspaces}
          defaultWorkspaceId={createBoardIn ?? undefined}
          busy={createBoard.isPending}
          error={createBoard.error?.message ?? null}
          onSubmit={({ workspaceId, title, background, visibility }) => {
            /*
             * ⚠ **One channel per event** (`D3-BRIEF.md` §5.7): a created board
             * is `announce()`d, because the surface it produces is a
             * *navigation*, which announces nothing by itself. The failure
             * channel is `useCreateBoard`'s own `onError`, which announces the
             * rollback — so nothing here renders the error through a second
             * live region; `BoardCreateForm` shows it on the field, which is
             * `aria-describedby` and not a live region.
             *
             * `announce()` before the navigation, not after: the live region
             * lives in `__root.tsx` and survives the route change, but the
             * sentence has to be written while this handler still owns the
             * turn — `FR-BOARD-001` AC2's "opened directly on the board face"
             * is a navigation, and a navigation announces nothing by itself.
             */
            /*
             * ⚠ **`visibility` IS SPREAD-OMITTED, LIKE `background`, AND HERE
             * THE OMISSION IS THE CONTRACT** — `BE-09.4` / `FR-BOARD-001` AC3.
             * `BoardCreateForm` emits the key only when the member actually
             * changed the control, and this host must forward that absence
             * rather than resolve it: the server answers an omitted key with
             * `enterpriseDefaultBoardVisibility`, which for an
             * Enterprise-linked workspace can be a level that is not
             * `workspace_visible` (`FR-PERM-033` AC4). Spelling
             * `visibility: visibility ?? 'workspace_visible'` here would
             * freeze the rendered default into every request and silently
             * override that Enterprise default — a 201 with the wrong level
             * stored and nothing red anywhere. Dropping the key entirely, as
             * this destructure did until now, is the same defect from the
             * other side: the control would move and the board would not.
             */
            createBoard.mutate(
              {
                workspaceId,
                title,
                ...(background === undefined ? {} : { background }),
                ...(visibility === undefined ? {} : { visibility }),
              },
              {
                onSuccess: (board) => {
                  announce(t('home.announce.boardCreated', { board: board.title }));
                  closeCreateBoard();
                  // `href`, not `to`: `to` is matched against the route-path
                  // union and cannot take an assembled string. `boardPath`
                  // comes from `packages/shared` — the same builder the server
                  // uses for `boardAddressSchema.path` (§5.4), so this never
                  // becomes a second spelling of the canonical address.
                  void navigate({ href: boardPath(board.shortLink, board.slug) });
                },
              },
            );
          }}
        />
      </Dialog>
    </>
  );
}

/**
 * `?workspace=<id>` scopes the page to one group — `D2-BRIEF.md` §5.8, and it
 * is what makes `FR-PERM-004` AC3's *"board lists … fully separate"* half
 * assertable in a browser without inventing a per-workspace board page.
 * Absent or malformed means every group, which is `FR-NOTIF-029`'s specified
 * default unchanged.
 *
 * ⚠ **A well-formed ULID naming a workspace the member is not in falls back to
 * every group**, rather than to an empty page. `workspaceScopeFrom` can only
 * prove the *shape* of the value, so a stale bookmark, a deleted workspace and
 * a hand-typed id all arrive here looking valid. The alternative — zero
 * groups — renders the first-run *"create your first workspace"* copy at a
 * member who has several, which is a worse lie than ignoring a filter that
 * matches nothing. It is also self-healing after a workspace is deleted.
 */
function scopeToWorkspace(
  groups: readonly LandingGroup[],
  workspaceId: string | null,
): LandingGroup[] {
  if (workspaceId === null) return [...groups];
  const scoped = groups.filter((group) => group.workspaceId === workspaceId);
  return scoped.length === 0 ? [...groups] : scoped;
}
